import React, {
  ComponentProps,
  ComponentPropsWithoutRef,
  memo,
  PropsWithChildren,
  ReactNode,
  useRef,
} from 'react'

import styled from '@emotion/styled'

import { isFunction } from '@surglogs/util.is-function'

import { useElementDimensions } from '@surglogs-app/hooks'
import { Box, Button, ButtonProps, Colors, FlexBox } from '@surglogs-app/ui'

import { Modal } from './Modal'
import { MAX_MODAL_HEIGHT } from './ModalContent'

export type DefaultModalVariant = 'default' | 'neutral'

export interface DefaultModalProps
  extends Omit<ComponentPropsWithoutRef<typeof Modal>, 'children'>,
    PropsWithChildren {
  footerChildren?: ReactNode
  onCancelButtonClick?: null | (() => void)
  cancelButtonLabel?: string | ReactNode
  contentProps?: Partial<ComponentProps<typeof DefaultModalContentDynamic>>
  variant?: DefaultModalVariant
  withFixedHeight?: boolean
}

const getContentBackgroundColor = (variant: DefaultModalVariant): Colors => {
  if (variant === 'neutral') {
    return 'background'
  }

  return 'white.100'
}

export const BaseContent = (props: ComponentProps<typeof Box>) => (
  <Box
    {...{
      padding: 'xl',
      overflowY: 'auto',
      ...props,
    }}
  />
)

// These hardcoded offsets create edge cases where layout is broken.
// Used all over the codebase as standalone component so we can't change it easily.
/** @deprecated use DefaultModal */
export const DefaultModalContent = styled(BaseContent)`
  max-height: calc(100vh - 450px);

  @media screen and (min-height: 1100px) {
    max-height: calc(100vh - 500px);
  }
`

const DefaultModalContentDynamic = styled(BaseContent)<{
  offset: number
  withFixedHeight?: boolean
}>`
  max-height: calc(${MAX_MODAL_HEIGHT} - ${({ offset }) => offset}px);
  ${({ withFixedHeight, offset }) =>
    withFixedHeight
      ? `
    height:  calc(${MAX_MODAL_HEIGHT} - ${offset}px);
    overflow: hidden;
  `
      : ``}
`

export const DefaultModalButtons = styled(FlexBox)`
  border-top: 1px solid ${({ theme }) => theme.ui.colors['navy.10']};
  gap: ${({ theme }) => theme.ui.spacings.sm};
  padding: ${({ theme }) => theme.ui.spacings.xl};
`

export const DefaultModalButtonsCancel = memo(
  (props: Omit<ButtonProps, 'ref'>) => (
    <Button {...{ 'aria-label': 'cancel', variant: 'secondary' }} {...props} />
  ),
)

export const DefaultModal = memo(
  ({
    hideModal,
    children,
    footerChildren,
    onCancelButtonClick = hideModal,
    cancelButtonLabel = 'Cancel',
    contentProps,
    variant = 'default',
    withFixedHeight = false,
    ...rest
  }: DefaultModalProps) => {
    const headerRef = useRef<HTMLDivElement>(null)
    const footerRef = useRef<HTMLDivElement>(null)
    const { height: headerHeight } = useElementDimensions({ ref: headerRef })
    const { height: footerHeight } = useElementDimensions({ ref: footerRef })

    return (
      <Modal {...rest} {...{ hideModal, headerRef }}>
        <DefaultModalContentDynamic
          {...{
            offset: headerHeight + footerHeight,
            bgc: getContentBackgroundColor(variant),
            className: 'modal-content',
            withFixedHeight,
            ...contentProps,
          }}>
          {children}
        </DefaultModalContentDynamic>
        <DefaultModalButtons {...{ ref: footerRef, className: 'modal-footer' }}>
          {footerChildren}
          {isFunction(onCancelButtonClick) ? (
            <DefaultModalButtonsCancel {...{ onClick: onCancelButtonClick }}>
              {cancelButtonLabel}
            </DefaultModalButtonsCancel>
          ) : null}
        </DefaultModalButtons>
      </Modal>
    )
  },
)
